import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, EmptyState } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Store, Plus, RefreshCw, Trash2, ShoppingBag, CheckCircle2, AlertCircle, Loader2, Link2, KeyRound } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { can } from "@/lib/permissions";

const STATUS = {
  connected: { label: "Connected", cls: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle2 },
  error: { label: "Needs attention", cls: "bg-red-50 text-red-700 border-red-200", icon: AlertCircle },
  disconnected: { label: "Disconnected", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: AlertCircle },
};
const TYPE_LABEL = { shopify: "Shopify", woocommerce: "WooCommerce", custom: "Custom / API" };

const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

const emptyForm = {
  name: "",
  // shopify
  shop_domain: "", access_token: "", api_secret: "",
  // woocommerce
  store_url: "", consumer_key: "", consumer_secret: "",
  default_pickup_location_id: "", auto_sync: true,
  dead_weight_kg: "0.5", length_cm: "15", breadth_cm: "12", height_cm: "8",
};
const TYPE_TITLE = { shopify: "Connect Shopify", woocommerce: "Connect WooCommerce", magento: "Connect Magento", custom: "Connect Custom / API" };

const sampleCurl = (r) => `curl -X POST '${r.post_url}' \\
  -H 'X-EdgeLMS-Channel-Key: ${r.api_key}' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "order_id": "YOUR-ORDER-ID",
    "payment_mode": "cod",
    "buyer": { "name": "Asha", "phone": "9876543210", "pincode": "560001",
               "address": "12 MG Road", "city": "Bengaluru", "state": "Karnataka" },
    "products": [ { "name": "Tee", "sku": "TEE-1", "unit_price": 499, "qty": 1 } ]
  }'`;

const B2CChannels = ({ user, onLogout }) => {
  const canManage = can(user, "b2c.channels:manage");

  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState([]);
  const [pickups, setPickups] = useState([]);

  const [pickType, setPickType] = useState(false);   // "choose a channel" modal
  const [connectType, setConnectType] = useState(null);  // "shopify" | "woocommerce" | "magento" | "custom" | null
  const [customResult, setCustomResult] = useState(null); // {api_key, post_url} shown once after custom connect
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [busy, setBusy] = useState(null);            // `${id}:${action}`

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/b2c/seller/channels`);
      setChannels(data.items || []);
    } catch (e) {
      toast.error(errDetail(e, "Failed to load channels"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChannels();
    axios.get(`${API}/b2c/seller/channels/catalog`).then((r) => setCatalog(r.data.catalog || [])).catch(() => {});
    axios.get(`${API}/pickup-locations/`).then((r) => setPickups(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [fetchChannels]);

  const connectGuide = (catalog.find((c) => c.type === connectType) || {}).guide || { steps: [], note: "" };

  const openConnect = (type) => { setForm(emptyForm); setCustomResult(null); setPickType(false); setConnectType(type); };
  const closeConnect = () => { setConnectType(null); setCustomResult(null); };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submitConnect = async () => {
    const pkg = {
      dead_weight_kg: parseFloat(form.dead_weight_kg) || 0,
      length_cm: parseFloat(form.length_cm) || 0,
      breadth_cm: parseFloat(form.breadth_cm) || 0,
      height_cm: parseFloat(form.height_cm) || 0,
    };
    let body;
    if (connectType === "shopify") {
      if (!form.name.trim() || !form.shop_domain.trim() || !form.access_token.trim() || !form.default_pickup_location_id) {
        toast.error("Store name, domain, access token and pickup location are required.");
        return;
      }
      body = {
        name: form.name.trim(), shop_domain: form.shop_domain.trim(),
        access_token: form.access_token.trim(), api_secret: form.api_secret.trim() || null,
        default_pickup_location_id: form.default_pickup_location_id, auto_sync: form.auto_sync, default_package: pkg,
      };
    } else if (connectType === "woocommerce") {
      if (!form.name.trim() || !form.store_url.trim() || !form.consumer_key.trim() || !form.consumer_secret.trim() || !form.default_pickup_location_id) {
        toast.error("Store name, URL, consumer key & secret and pickup location are required.");
        return;
      }
      body = {
        name: form.name.trim(), store_url: form.store_url.trim(),
        consumer_key: form.consumer_key.trim(), consumer_secret: form.consumer_secret.trim(),
        default_pickup_location_id: form.default_pickup_location_id, auto_sync: form.auto_sync, default_package: pkg,
      };
    } else if (connectType === "magento") {
      if (!form.name.trim() || !form.store_url.trim() || !form.access_token.trim() || !form.default_pickup_location_id) {
        toast.error("Store name, URL, access token and pickup location are required.");
        return;
      }
      body = {
        name: form.name.trim(), store_url: form.store_url.trim(), access_token: form.access_token.trim(),
        default_pickup_location_id: form.default_pickup_location_id, auto_sync: form.auto_sync, default_package: pkg,
      };
    } else {
      // custom — no store credentials; EdgeLMS mints an API key
      if (!form.name.trim() || !form.default_pickup_location_id) {
        toast.error("Channel name and pickup location are required.");
        return;
      }
      body = { name: form.name.trim(), default_pickup_location_id: form.default_pickup_location_id, auto_sync: false, default_package: pkg };
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/b2c/seller/channels/${connectType}`, body);
      if (connectType === "custom") {
        setCustomResult({ api_key: data.api_key, post_url: `${API}${data.post_path}` });
        fetchChannels();
      } else {
        const synced = data?.initial_sync?.synced;
        toast.success(`${TYPE_LABEL[connectType] || "Channel"} connected${synced != null ? ` — ${synced} order(s) synced` : ""}`);
        setConnectType(null);
        fetchChannels();
      }
    } catch (e) {
      toast.error(errDetail(e, `Could not connect ${TYPE_LABEL[connectType] || "the channel"}`));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text) => { navigator.clipboard?.writeText(text).then(() => toast.success("Copied"), () => {}); };

  const syncNow = async (ch) => {
    setBusy(`${ch.id}:sync`);
    try {
      const { data } = await axios.post(`${API}/b2c/seller/channels/${ch.id}/sync`);
      toast.success(`Synced — ${data.synced} new, ${data.skipped} already imported`);
      fetchChannels();
    } catch (e) {
      toast.error(errDetail(e, "Sync failed"));
    } finally {
      setBusy(null);
    }
  };

  const toggleAutoSync = async (ch) => {
    setBusy(`${ch.id}:toggle`);
    try {
      await axios.put(`${API}/b2c/seller/channels/${ch.id}`, { auto_sync: !ch.auto_sync });
      fetchChannels();
    } catch (e) {
      toast.error(errDetail(e, "Could not update auto-sync"));
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async (ch) => {
    if (!window.confirm(`Disconnect ${ch.name}? New orders will stop syncing. Already-imported orders are kept.`)) return;
    setBusy(`${ch.id}:del`);
    try {
      await axios.delete(`${API}/b2c/seller/channels/${ch.id}`);
      toast.success("Channel disconnected");
      fetchChannels();
    } catch (e) {
      toast.error(errDetail(e, "Could not disconnect"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader
          title="Sales Channels"
          subtitle="Connect your store to auto-sync orders into B2C — review and ship from B2C Orders"
          actions={canManage ? <Button onClick={() => setPickType(true)}><Plus className="w-4 h-4 mr-1.5" /> Connect a channel</Button> : null}
        />

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : channels.length === 0 ? (
          <Card className="border-0 shadow-sm"><CardContent className="p-0">
            <EmptyState icon={Store} title="No channels connected" description="Connect Shopify to pull your store orders into EdgeLMS automatically." action={canManage ? <Button onClick={() => setPickType(true)}><Plus className="w-4 h-4 mr-1.5" /> Connect a channel</Button> : undefined} />
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {channels.map((ch) => {
              const st = STATUS[ch.status] || STATUS.disconnected;
              const StIcon = st.icon;
              return (
                <Card key={ch.id} className="border-0 shadow-sm">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center shrink-0"><ShoppingBag className="w-5 h-5 text-teal-600" /></div>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-800 truncate">{ch.name}</div>
                          <div className="text-xs text-slate-500">{TYPE_LABEL[ch.type] || ch.type} · {ch.channel_id}</div>
                          {ch.store_url ? <div className="text-xs text-slate-400 truncate flex items-center gap-1"><Link2 className="w-3 h-3" />{ch.store_url}</div>
                            : ch.type === "custom" ? <div className="text-xs text-slate-400 flex items-center gap-1"><KeyRound className="w-3 h-3" /> API push channel</div> : null}
                        </div>
                      </div>
                      <Badge variant="outline" className={st.cls}><StIcon className="w-3 h-3 mr-1" />{st.label}</Badge>
                    </div>

                    {ch.last_error && <p className="mt-3 text-xs text-red-600 bg-red-50 rounded px-2 py-1.5">{ch.last_error}</p>}

                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div><div className="text-slate-500 text-xs">Orders synced</div><div className="font-semibold text-slate-800">{ch.stats?.orders_synced ?? 0}</div></div>
                      <div><div className="text-slate-500 text-xs">Last sync</div><div className="text-slate-700">{ch.last_synced_at ? formatToIST(ch.last_synced_at) : "—"}</div></div>
                    </div>

                    <div className="mt-4 flex items-center justify-between border-t pt-3">
                      {ch.type === "custom" ? (
                        <span className="text-xs text-slate-500">Orders pushed via API</span>
                      ) : (
                        <label className="flex items-center gap-2 text-sm text-slate-600">
                          <Switch checked={!!ch.auto_sync} disabled={!canManage || busy === `${ch.id}:toggle`} onCheckedChange={() => toggleAutoSync(ch)} />
                          Auto-sync
                        </label>
                      )}
                      {canManage && (
                        <div className="flex items-center gap-1.5">
                          {ch.type !== "custom" && (
                            <Button variant="outline" size="sm" onClick={() => syncNow(ch)} disabled={busy === `${ch.id}:sync`}>
                              {busy === `${ch.id}:sync` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}<span className="ml-1.5">Sync now</span>
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => disconnect(ch)} disabled={busy === `${ch.id}:del`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Choose a channel */}
      <Dialog open={pickType} onOpenChange={setPickType}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Connect a channel</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {catalog.map((c) => (
              <button key={c.type} disabled={!c.available} onClick={() => c.available && openConnect(c.type)}
                className={`w-full flex items-center justify-between rounded-lg border p-3 text-left transition-colors ${c.available ? "border-slate-200 hover:border-teal-300 hover:bg-teal-50/40" : "border-slate-100 opacity-60 cursor-not-allowed"}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><ShoppingBag className="w-4 h-4 text-slate-600" /></div>
                  <div>
                    <div className="font-medium text-slate-800">{c.label}</div>
                    <div className="text-xs text-slate-500">{c.tagline}</div>
                  </div>
                </div>
                {c.available ? <Badge variant="outline" className="border-teal-200 text-teal-700">Available</Badge> : <Badge variant="outline" className="border-slate-200 text-slate-400">Soon</Badge>}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Connect a channel (store pull, or Custom push) */}
      <Dialog open={!!connectType} onOpenChange={(v) => !v && closeConnect()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-teal-600" /> {TYPE_TITLE[connectType] || "Connect a channel"}</DialogTitle></DialogHeader>

          {customResult ? (
            /* Custom: the generated key + endpoint, shown ONCE */
            <div className="space-y-4">
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                <KeyRound className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Save these now — the API key is shown only once. Have your system POST orders to the endpoint below.</span>
              </div>
              <div className="space-y-1.5">
                <Label>API key</Label>
                <div className="flex gap-2">
                  <Input readOnly value={customResult.api_key} className="font-mono text-xs" data-testid="custom-api-key" />
                  <Button variant="outline" onClick={() => copy(customResult.api_key)}>Copy</Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Orders endpoint (POST)</Label>
                <div className="flex gap-2">
                  <Input readOnly value={customResult.post_url} className="font-mono text-xs" />
                  <Button variant="outline" onClick={() => copy(customResult.post_url)}>Copy</Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between"><Label>Sample request</Label><Button variant="ghost" size="sm" onClick={() => copy(sampleCurl(customResult))}>Copy</Button></div>
                <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs overflow-x-auto font-mono whitespace-pre">{sampleCurl(customResult)}</pre>
              </div>
              <p className="text-xs text-slate-500">Header <code className="font-mono">X-EdgeLMS-Channel-Key</code> · body <code className="font-mono">order_id</code> dedupes. Full reference: admin → Developer Docs → Custom / API. Lost the key? Disconnect and reconnect for a new one.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Guide */}
              <div className="order-2 md:order-1 rounded-lg bg-slate-50 p-4">
                <p className="font-semibold text-slate-800 mb-2">{connectGuide.title || "How to connect"}</p>
                <ol className="space-y-2 text-sm text-slate-600 list-decimal list-inside">
                  {(connectGuide.steps || []).map((s, i) => <li key={i}>{s}</li>)}
                </ol>
                {connectGuide.note && <p className="mt-3 text-xs text-slate-500 border-t pt-2">{connectGuide.note}</p>}
              </div>
              {/* Form */}
              <div className="order-1 md:order-2 space-y-3">
                <div className="space-y-1.5"><Label>{connectType === "custom" ? "Channel name" : "Store name"}</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={connectType === "custom" ? "My system / ERP" : "My store"} /></div>

                {connectType === "shopify" ? (
                  <>
                    <div className="space-y-1.5"><Label>Store domain</Label><Input value={form.shop_domain} onChange={(e) => set("shop_domain", e.target.value)} placeholder="your-store.myshopify.com" /></div>
                    <div className="space-y-1.5"><Label>Admin API access token</Label><Input type="password" value={form.access_token} onChange={(e) => set("access_token", e.target.value)} placeholder="shpat_…" /></div>
                    <div className="space-y-1.5"><Label>API secret <span className="text-slate-400">(optional — for real-time webhooks)</span></Label><Input type="password" value={form.api_secret} onChange={(e) => set("api_secret", e.target.value)} placeholder="shpss_…" /></div>
                  </>
                ) : connectType === "woocommerce" ? (
                  <>
                    <div className="space-y-1.5"><Label>Store URL</Label><Input value={form.store_url} onChange={(e) => set("store_url", e.target.value)} placeholder="https://your-store.com" /></div>
                    <div className="space-y-1.5"><Label>Consumer key</Label><Input type="password" value={form.consumer_key} onChange={(e) => set("consumer_key", e.target.value)} placeholder="ck_…" /></div>
                    <div className="space-y-1.5"><Label>Consumer secret</Label><Input type="password" value={form.consumer_secret} onChange={(e) => set("consumer_secret", e.target.value)} placeholder="cs_…" /></div>
                  </>
                ) : connectType === "magento" ? (
                  <>
                    <div className="space-y-1.5"><Label>Store URL</Label><Input value={form.store_url} onChange={(e) => set("store_url", e.target.value)} placeholder="https://your-store.com" /></div>
                    <div className="space-y-1.5"><Label>Integration access token</Label><Input type="password" value={form.access_token} onChange={(e) => set("access_token", e.target.value)} placeholder="Magento access token" /></div>
                  </>
                ) : null}

                <div className="space-y-1.5">
                  <Label>Default pickup location</Label>
                  <Select value={form.default_pickup_location_id} onValueChange={(v) => set("default_pickup_location_id", v)}>
                    <SelectTrigger><SelectValue placeholder="Select a pickup location" /></SelectTrigger>
                    <SelectContent>
                      {pickups.map((p) => <SelectItem key={p.location_id} value={p.location_id}>{p.facility_name || p.company_name || "Pickup"} — {p.city || ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Default package <span className="text-slate-400">(fallback when an order has no weight)</span></Label>
                  <div className="grid grid-cols-4 gap-2">
                    <Input value={form.dead_weight_kg} onChange={(e) => set("dead_weight_kg", e.target.value)} placeholder="kg" inputMode="decimal" />
                    <Input value={form.length_cm} onChange={(e) => set("length_cm", e.target.value)} placeholder="L cm" inputMode="decimal" />
                    <Input value={form.breadth_cm} onChange={(e) => set("breadth_cm", e.target.value)} placeholder="B cm" inputMode="decimal" />
                    <Input value={form.height_cm} onChange={(e) => set("height_cm", e.target.value)} placeholder="H cm" inputMode="decimal" />
                  </div>
                </div>
                {connectType !== "custom" && (
                  <label className="flex items-center gap-2 text-sm text-slate-700 pt-1">
                    <Switch checked={form.auto_sync} onCheckedChange={(v) => set("auto_sync", v)} /> Auto-sync new orders
                  </label>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            {customResult ? (
              <Button onClick={closeConnect}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={closeConnect} disabled={submitting}>Cancel</Button>
                <Button onClick={submitConnect} disabled={submitting}>{submitting ? "Connecting…" : (TYPE_TITLE[connectType] || "Connect")}</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SellerLayout>
  );
};

export default B2CChannels;
